import { ServerConfig } from "@/config/config";
import { Purchase } from "@/models/base";
import { IUblPurchaseWrapper } from "@/plugins/ubl/services/ubl-service";
import { Axios } from "@/utils/axios";
import { IGetResponse } from "@/utils/get-response";
import { InboxreaderPurchase } from "@/utils/purchase/inboxreader-purchase";
import { InboxreaderPurchaseFile } from "@/utils/purchase/inboxreader-purchase-file";
import { InboxreaderLabel } from "@/utils/purchase/inboxreader-label";
import { Job } from "@/models/job";
import { downloadBlob } from "@/utils/blob";

export type InboxReaderType = "peppol" | "inbox"
export interface IInboxReaderFetchOptions {
	types?:InboxReaderType[]
	processed?:boolean;
	search?:string;
	labels?:number[];
	limit?:number;
	offset?:number;
}


export type InboxUploadSkipReason = "queued" | "inbox";

export interface ISkippedUploadFile {
	Name:string;
	Reason:InboxUploadSkipReason;
}

export interface IInboxUploadResult {
	job:Job;
	skippedFiles:ISkippedUploadFile[];
}

export interface IInboxDocumentCounts {
	peppol:number;
	other:number;
}

class InboxreaderServiceClass {
	private readonly inboxreaderUrl:string = `${ServerConfig.host}/inboxreader`

	public async getInboxreaderUnprocessedFiles():Promise<InboxreaderPurchaseFile[]>{
		let result = await Axios.get(this.inboxreaderUrl+"/list-files");
		return (result.data || []).map((r:any)=>new InboxreaderPurchaseFile(r));
	}

	public async getInboxreaderPurchase(id:number):Promise<InboxreaderPurchase>{
		let result = await Axios.get(`${this.inboxreaderUrl}/${id}`);
		return new InboxreaderPurchase(result.data);
	}

	public async getInboxreaderPurchaseWithPeppolOrder(id:number):Promise<Purchase>{
		let result = await Axios.get(`${this.inboxreaderUrl}/${id}/peppol-order`);
		return new Purchase(result.data.Purchase);
	}

	public async checkIfPeppolOrderContactExists(purchaseId:number):Promise<boolean>{
		let result = await Axios.get(`${this.inboxreaderUrl}/${purchaseId}/peppol-order/contact-exists`);
		return result.data.Exists || false;
	}

	public async deleteInboxreaderPurchase(id:number):Promise<void>{
		await Axios.delete(`${this.inboxreaderUrl}/${id}`);
	}

	public async archiveInboxreaderPurchase(id:number):Promise<void>{
		await Axios.post(`${this.inboxreaderUrl}/${id}/mark-as-processed`);
	}

	public async deleteInboxreaderPurchaseFile(id:number, fileId:number):Promise<void>{
		await Axios.delete(`${this.inboxreaderUrl}/${id}/file/${fileId}`);
	}
	public async archiveInboxreaderPurchaseFile(id:number, fileId:number):Promise<void>{
		await Axios.post(`${this.inboxreaderUrl}/${id}/file/${fileId}/mark-as-processed`);
	}
	public async unarchiveInboxreaderPurchase(id:number):Promise<void>{
		await Axios.post(`${this.inboxreaderUrl}/${id}/mark-as-unprocessed`);
	}
	public async unarchiveInboxreaderPurchaseFile(id:number, fileId:number):Promise<void>{
		await Axios.post(`${this.inboxreaderUrl}/${id}/file/${fileId}/mark-as-unprocessed`);
	}

	public async approveInboxreaderPurchaseFile(id:number, fileId:number):Promise<void>{
		await Axios.post(`${this.inboxreaderUrl}/${id}/file/${fileId}/approve`);
	}

	public async unapproveInboxreaderPurchaseFile(id:number, fileId:number):Promise<void>{
		await Axios.post(`${this.inboxreaderUrl}/${id}/file/${fileId}/unapprove`);
	}

	public async getInboxReaderAlias():Promise<string>{
		let result = await Axios.get(`${this.inboxreaderUrl}/alias`);
		return result.data as string;
	}

	public async isInboxReaderAliasAvailable(alias:string):Promise<boolean>{
		let result = await Axios.get(`${this.inboxreaderUrl}/is-alias-available/${alias}`);
		return result.data as boolean;
	}

	public async setInboxReaderAlias(alias:string):Promise<string>{
		let result = await Axios.post(`${this.inboxreaderUrl}/set-alias/${alias}`);
		return result.data as string;
	}

	public async getInboxreaderPurchases(opts:IInboxReaderFetchOptions = {}):Promise<IGetResponse<InboxreaderPurchase>>{
		let result = await Axios.get(this.inboxreaderUrl, {params: opts});
		return {
			data: result.data.data.map((r:any)=>new InboxreaderPurchase(r)),
			records: result.data.records
		};
	}

	// fetchDocumentCounts returns how many cards the unified inbox shows per
	// source: one per peppol document, one per file of a mail or upload.
	public async fetchDocumentCounts(opts:IInboxReaderFetchOptions):Promise<IInboxDocumentCounts>{
		let result = await Axios.get(`${this.inboxreaderUrl}/count-documents`, {params: opts});
		return {
			peppol: result.data.Peppol || 0,
			other: result.data.Other || 0
		};
	}

	public async fetchCountFiles(opts:IInboxReaderFetchOptions):Promise<number>{
		let result = await Axios.get(`${this.inboxreaderUrl}/count-files`, {params: opts});
		return result.data.Count;
	}

	public async setNoteForBookkeeper(id:number, note:string):Promise<void>{
		await Axios.post(`${this.inboxreaderUrl}/${id}/set-note-for-bookkeeper`, {Note: note});
	}

	public async getLabels():Promise<InboxreaderLabel[]>{
		let result = await Axios.get(`${this.inboxreaderUrl}/labels`);
		return (result.data || []).map((r:any)=>new InboxreaderLabel(r));
	}

	public async createLabel(name:string, color:string):Promise<InboxreaderLabel>{
		let result = await Axios.post(`${this.inboxreaderUrl}/labels`, {Name: name, Color: color});
		return new InboxreaderLabel(result.data);
	}

	public async updateLabel(id:number, name:string, color:string):Promise<InboxreaderLabel>{
		let result = await Axios.post(`${this.inboxreaderUrl}/labels/${id}`, {Name: name, Color: color});
		return new InboxreaderLabel(result.data);
	}

	public async deleteLabel(id:number):Promise<void>{
		await Axios.delete(`${this.inboxreaderUrl}/labels/${id}`);
	}

	public async assignLabel(labelId:number, fileIds:number[]):Promise<void>{
		await Axios.post(`${this.inboxreaderUrl}/labels/${labelId}/assign`, {FileIDs: fileIds});
	}

	public async unassignLabel(labelId:number, fileIds:number[]):Promise<void>{
		await Axios.post(`${this.inboxreaderUrl}/labels/${labelId}/unassign`, {FileIDs: fileIds});
	}

	public async downloadUblForBookkeeper(purchaseId:number):Promise<File>{
		let result = await Axios.get(`${this.inboxreaderUrl}/${purchaseId}/ubl-for-bookkeeper`, {responseType: "blob"});
		console.log(result.headers);
		let fileNameHeader = result.headers["content-disposition"];
		let fileNameValue = fileNameHeader.match(/filename=(?:UTF-8''|"?)([^";]+)"?/i);
		console.log(fileNameValue);
		let fileName = fileNameValue[1] ? decodeURIComponent(fileNameValue[1]) : "download.xml";
		return new File([result.data], fileName, {type: "application/xml"});
	}

	// uploadFiles posts the files and reports how much of them is sent, so the
	// inbox can show the progress of the upload itself.
	public async uploadFiles(files:File[], onProgress?:(percent:number)=>void):Promise<IInboxUploadResult>{
		let formData = new FormData();
		for (let file of files) {
			formData.append("file", file, file.name);
		}
		let result = await Axios.post(`${this.inboxreaderUrl}/upload`, formData, {
			onUploadProgress: (event)=>{
				if (!onProgress || !event.total) return;
				onProgress(Math.round((event.loaded / event.total) * 100));
			}
		});
		return {
			job: new Job(result.data),
			skippedFiles: result.data.SkippedFiles || []
		};
	}

	public async batchSendToBookkeeper(purchaseIds:number[]):Promise<Job>{
		let result = await Axios.post(`${this.inboxreaderUrl}/batch-send-to-bookkeeper`, purchaseIds);
		return new Job(result.data);
	}

	public async downloadZip(purchaseIds:number[], fileIds:number[]):Promise<void>{
		let body = {PurchaseIDs: purchaseIds, FileIDs: fileIds};
		let result = await Axios.post(`${this.inboxreaderUrl}/download-zip`, body, {responseType: "blob"});
		let fileName = "inbox.zip";
		let header:string = result.headers["content-disposition"] || "";
		let match = header.match(/filename=(?:UTF-8''|"?)([^";]+)"?/i);
		if (match && match[1]) {
			fileName = decodeURIComponent(match[1]);
		}
		downloadBlob(new Blob([result.data], {type: "application/zip"}), fileName);
	}

	public async purchaseFromSelfBilledSale(purchaseDocumentId:number):Promise<Purchase>{
		let result = await Axios.get(`${this.inboxreaderUrl}/from-self-billed-sale/${purchaseDocumentId}`);
		let data:IUblPurchaseWrapper = result.data;
		let purchase = new Purchase(result.data.Purchase);
		purchase.UnprocessedFiles = data.Attachments.map(a=>{
			let bytes = Uint8Array.from(atob(a.FileContent), c=>c.charCodeAt(0));
			return new File([bytes], a.FileName, {type: a.MimeType});
		});
		return purchase;
	}
}

export const InboxreaderService = new InboxreaderServiceClass();